/* @jsx h */
import { useMenu } from "@app/vue/providers/index";
import { calculateCartSubtotal } from "@lemmonhq/orders";
import { Product, ProductPromotionResult } from "@lemmonhq/types";
import { defineComponent, ref, watch, h, onMounted } from "vue";
import { RouterLink } from "vue-router";
import { MenuHeader } from "./MenuHeader";
import { MenuList } from "./MenuList";
import { MenuProductSelectModal } from "./MenuProductSelectModal";
import { useLocalization } from "@lemmonhq/uikit";

export const ShopMenuPage = defineComponent({
  setup() {
    const { products, services, promotions, cart, categories, shop } =
      useMenu();

    const selectedProduct = ref<Product | null>(null);
    const isProductModalOpen = ref(false);
    const activeCategory = ref<number | null>(null);

    const handleProductClick = (product: Product) => {
      selectedProduct.value = product;
      isProductModalOpen.value = true;
    };

    const handleProductConfirm = (
      product: Product,
      quantity: number,
      promotionResult: ProductPromotionResult,
      selectedBundles?: { bundleId: number; optionIds: number[] }[],
      notes?: string,
    ) => {
      console.log("selectedBundles", selectedBundles);

      const formattedBundles = selectedBundles?.map(
        ({ bundleId, optionIds }) => {
          const bundle = (product!.bundles || []).find(
            (b) => b.id === bundleId,
          )!;
          return {
            bundleId,
            name: bundle.name,
            options: optionIds.map((optionId) => {
              const option = bundle.options.find((o) => o.id === optionId)!;
              return {
                id: option.id,
                title: option.display.title,
                entity_id: option.entity_id,
                entity_type: option.entity_type,
                price: option.price,
              };
            }),
          };
        },
      );
      if (selectedProduct.value) {
        cart.addToCart(
          product,
          formattedBundles,
          undefined,
          quantity,
          notes,
          promotionResult.hasDiscountPromotions ? promotionResult : undefined,
        );
      }
      isProductModalOpen.value = false;
      selectedProduct.value = null;
    };

    const handleCategoryChange = (categoryId: number) => {
      activeCategory.value = categoryId;
    };
    onMounted(() => {
      // Cart setup is now handled by parent ShopGroupOrder component
      cart.cart.checkoutState = "ordering";
    });
    return () => {
      return (
        <>
          <MenuHeader
            categories={categories.value}
            activeCategory={activeCategory.value}
          />
          <MenuList
            categories={categories.value}
            products={products.value}
            onProductSelect={handleProductClick}
            onCategoryChange={handleCategoryChange}
          />
          <CartButton />
          {selectedProduct.value && isProductModalOpen.value && (
            <MenuProductSelectModal
              services={services.value}
              promotions={promotions.value}
              product={selectedProduct.value}
              onClose={() => {
                isProductModalOpen.value = false;
                selectedProduct.value = null;
              }}
              onConfirm={handleProductConfirm}
            />
          )}
        </>
      );
    };
  },
});

const CartButton = defineComponent<{}>({
  name: "CartButton",
  props: [],
  setup(props) {
    const { cart, shop, isRestaurantOpen, groupOrder } = useMenu();
    const { t } = useLocalization();
    const isVisible = ref(cart.cart.items.length > 0);

    watch(
      () => cart.cart.items.length,
      (newLength) => {
        isVisible.value = newLength > 0;
      },
    );

    const totalAmount = () => {
      return calculateCartSubtotal(cart.cart);
    };

    return () => {
      return (
        <>
          <div
            class={[
              "sticky bottom-4 left-4 right-4 transition-all duration-500 ease-out",
              isVisible.value
                ? "opacity-100 translate-y-0 pointer-events-auto"
                : "opacity-0 translate-y-full pointer-events-none",
            ]}
          >
            {cart.cart.items.length > 0 ? (
              <div class="px-2">
                <RouterLink
                  to={
                    isRestaurantOpen.value
                      ? `/restaurant/${shop.value.slug}/group-order/${groupOrder.value?.id}/checkout`
                      : "#"
                  }
                  class="w-full btn btn-primary rounded-md shadow-lg flex justify-between items-center px-6 py-4 max-w-[500px] mx-auto btn-lg"
                >
                  {isRestaurantOpen.value ? (
                    <>
                      <div class="flex items-center justify-center text-md">
                        <span class="material-symbols-outlined mr-2">
                          shopping_cart
                        </span>
                        <span>
                          {t(
                            cart.cart.items.length === 1
                              ? "customer.shop_menu.cart_item_count_one"
                              : "customer.shop_menu.cart_item_count_many",
                            [cart.cart.items.length],
                          )}
                        </span>
                      </div>
                      <div class="flex items-center justify-center">
                        <div>
                          {totalAmount().toFixed(2)} {shop.value.currency}
                        </div>
                        <span class="material-symbols-outlined mr-2">
                          arrow_forward_ios
                        </span>
                      </div>
                    </>
                  ) : (
                    <div class="flex flex-1 items-center text-center justify-center text-md">
                      {t("customer.shop_menu.restaurant_closed")}
                    </div>
                  )}
                </RouterLink>
              </div>
            ) : null}
          </div>
        </>
      );
    };
  },
});
